import { Tooltip } from "flowbite-react"
import React, { Dispatch, ReactNode, SetStateAction, useEffect, ReactElement } from "react"

import CustomImage from "@components/elements/CustomImage"
import DangerouslyHTMLTextPriview from "@components/elements/DangerouslyHTMLTextPriview"
import StripHTMLAndAddEllipses from "@components/elements/StripHTMLAndAddEllipses"
import useAuth from "@hooks/useAuth"
import MembershipIcon from "@icons/MemberShipIcon"
import { ProductInternalTypeEnum } from "@mono/essentials/constants/options"
import { CartProductType } from "@mono/essentials/types/CartTypes"
import { ProductCategoryType, ProductType } from "@mono/essentials/types/OrderTypes"
import { ProductCategoryTypes } from "@mono/essentials/types/OrderTypes"

interface CategoryProductPreviewProps {
  categoryList: ProductCategoryType[]
  selectedCategory: string
  setSelectedProduct?: Dispatch<SetStateAction<ProductType | undefined>>
  goToNextStep?: (category_id: string, product_id: string) => void
  setShow?: Dispatch<SetStateAction<boolean>>
  isModal?: boolean
  showDescription?: boolean
}

const productTypes = [
  ProductCategoryTypes.BOOKABLE,
  ProductCategoryTypes.MERCHANDIZE,
  ProductCategoryTypes.ADD_ONS,
  ProductCategoryTypes.GIFT_CARDS,
  ProductCategoryTypes.MEMBERSHIP,
]

const CategoryProductPreview = ({
  categoryList,
  selectedCategory,
  setSelectedProduct,
  goToNextStep,
  setShow,
  isModal,
  showDescription,
}: CategoryProductPreviewProps) => {
  const { isCustomerLoggedIn } = useAuth()
  const _all_products = categoryList?.filter(
    (_c: ProductCategoryType) => _c.id === selectedCategory
  )[0]?.activeProducts

  const filterProductsByType = (products: any[], productType: string) =>
    products
      ?.filter(
        (item) =>
          !item.merchandise ||
          item.merchandise.sell?.includes("individually") ||
          item.merchandise.length === 0
      )
      .filter((product) => product.type?.internal_type === productType) || []

  const showCategoryProducts = productTypes.reduce((result, type) => {
    const filteredProducts = filterProductsByType(_all_products, type)
    return [...result, ...filteredProducts]
  }, [])

  const handleProductSelect = (selectedProduct: ProductType) => {
    if (selectedProduct) {
      setSelectedProduct?.(selectedProduct)
      goToNextStep?.(selectedCategory, selectedProduct?.id)
      setShow?.(true)
    }
  }

  return (
    <>
      {showDescription
        ? (showCategoryProducts || [])?.map((product: ProductType, index: number) => (
            <div
              key={`${product.id}_${index}`}
              className={
                " flex h-full w-full cursor-pointer flex-col overflow-hidden rounded-lg border shadow-sm"
              }
            >
              <div
                className={` ${
                  product.categories[0].cover_image ? `` : "bg-imagePlaceholderBackground"
                } relative flex md:min-h-[200px] sm:min-h-[180px] min-h-[180px] md:min-w-[300px] sm:min-w-[250px] flex-1 flex-row items-end bg-cover bg-center overflow-hidden`}
                onClick={() => handleProductSelect(product)}
              >
                {!(product?.mediaFiles?.length && product.mediaFiles[0]?.public_url) ? (
                  <div
                    className={
                      "absolute left-1/2 top-1/2 h-11 w-[52px] -translate-x-1/2 -translate-y-1/2 transform bg-transparent bg-imagePlaceholder bg-contain bg-center bg-no-repeat"
                    }
                  />
                ) : (
                  <CustomImage
                    src={product?.mediaFiles.length && product?.mediaFiles[0]?.public_url}
                    className={"overflow-hidden rounded-t-lg"}
                    fill
                    objectFit={"cover"}
                  />
                )}
              </div>
              <div className={"m-4"}>
                <p className={"relative text16Semibold mb-1.5 drop-shadow-sm"}>{product.name}</p>
                <StripHTMLAndAddEllipses
                  content={product?.description}
                  maxLength={isModal ? 30 : 40}
                  className={"!text14Regular !mb-4 !leading-5 !text-neutral-600 !drop-shadow-sm"}
                />
                <div
                  className={
                    "text14Medium shadow-[0px 2px 5px rgba(42, 93, 227, 0.3)] mt-6 flex flex-row items-center justify-center gap-2 rounded-lg bg-primary-600 p-2.5 px-1 text-whiteBlack-100"
                  }
                  onClick={(e) => {
                    e.stopPropagation()
                    handleProductSelect(product)
                  }}
                >
                  {product?.type?.internal_type ===
                    (ProductInternalTypeEnum.MERCHANDISE as string) ||
                  product?.type?.internal_type === (ProductInternalTypeEnum.GIFT_CARD as string) ||
                  product?.type?.internal_type === (ProductInternalTypeEnum.MEMBERSHIP as string)
                    ? "Buy"
                    : "Book"}
                </div>
              </div>
            </div>
          ))
        : (showCategoryProducts || []).map((product: CartProductType, index: number) => {
            return (
              <div
                key={`${product.id}_${index}`}
                className={
                  "flex h-full min-h-[230px] sm:min-h-[230px]  md:min-w-50 sm:min-w-[250px] w-full cursor-pointer flex-col overflow-hidden rounded-lg shadow-sm"
                }
              >
                <div
                  className={`
                    ${
                      product?.mediaFiles?.length && product?.mediaFiles[0]?.public_url
                        ? ``
                        : "bg-imagePlaceholderBackground"
                    }
                    relative flex flex-1 flex-row
                    items-end bg-cover bg-center image-gradiant overflow-hidden
                  `}
                  onClick={() => handleProductSelect(product)}
                >
                  {!(product?.mediaFiles && product?.mediaFiles[0]?.public_url) ? (
                    <div
                      className={
                        "absolute left-1/2 top-1/2 h-11 w-[52px] -translate-x-1/2 -translate-y-1/2 transform bg-transparent bg-imagePlaceholder bg-contain bg-center bg-no-repeat"
                      }
                    />
                  ) : (
                    <CustomImage
                      src={product.mediaFiles.length && product.mediaFiles[0]?.public_url}
                      className={"overflow-hidden rounded-t-lg"}
                      fill
                      objectFit={"cover"}
                    />
                  )}
                  {product.visibility === "member" ? (
                    <div className={"absolute top-2 left-2"}>
                      <Tooltip
                        content={"Member Only Product"}
                        placement={"auto"}
                        className={
                          "!bg-[#1B1B1B] !text-white !border-0 !px-4 !py-2 !rounded-lg !text14Regular !w-44"
                        }
                        trigger={"hover"}
                      >
                        <div className={"p-1 rounded-lg bg-black/[.64] cursor-pointer"}>
                          <MembershipIcon className={"text-[#FFE601] h-5 w-5"} />
                        </div>
                      </Tooltip>
                    </div>
                  ) : null}
                  {product?.applicableMembershipDiscounts &&
                  product.visibility !== "member" &&
                  isCustomerLoggedIn ? (
                    <div className={"top-0 absolute py-1 px-2 mt-1.5 rounded-r-lg bg-black/[.64]"}>
                      <div className={"text14Semibold text-[#FFE601]"}>Member Discount</div>
                    </div>
                  ) : null}
                  {isModal ? (
                    <div
                      className={`flex flex-1 flex-row z-1 text-whiteBlack-100  ${
                        isModal
                          ? "absolute w-full rounded-b-lg bg-black/[.64] px-3 py-3"
                          : "mx-3 mb-3 mt-auto bg-transparent"
                      } items-center justify-between gap-2  text-whiteBlack-100`}
                    >
                      <div className={"w-full"}>
                        <div
                          className={`line-clamp-2 ${!product?.widget_description ? "py-1" : ""}`}
                        >
                          <StripHTMLAndAddEllipses
                            content={product?.name}
                            maxLength={75}
                            className={"text15Semibold"}
                          />
                        </div>
                        <div className={"line-clamp-2"}>
                          {product?.widget_description && (
                            <StripHTMLAndAddEllipses
                              content={product?.widget_description}
                              maxLength={100}
                              className={"text13Medium my-1"}
                            />
                          )}
                        </div>
                        <div className={"flex justify-between w-full items-center gap-3"}>
                          <div>
                            {product?.advertise_price && (
                              <StripHTMLAndAddEllipses
                                content={product?.advertise_price}
                                className={
                                  "paragraph13 border border-[#999BA2] rounded-sm w-fit py-0.5 px-1 bg-[#424450]"
                                }
                              />
                            )}

                            {product?.membership?.price && (
                              <div
                                className={
                                  "paragraph13 border border-[#999BA2] rounded-sm w-fit py-0.5 px-1 bg-[#424450]"
                                }
                              >
                                {product?.membership?.price?.price_formatted}
                              </div>
                            )}
                          </div>
                          <div
                            className={
                              "text14Medium shadow-[0px 2px 5px rgba(42, 93, 227, 0.3)] flex flex-row items-center justify-center gap-2 bg-primary-600 px-4 py-1.5 text-whiteBlack-100 rounded-md"
                            }
                            onClick={(e) => {
                              e.stopPropagation()
                              handleProductSelect(product)
                            }}
                          >
                            {product?.type?.internal_type ===
                              (ProductInternalTypeEnum.MERCHANDISE as string) ||
                            product?.type?.internal_type ===
                              (ProductInternalTypeEnum.GIFT_CARD as string) ||
                            product?.type?.internal_type ===
                              (ProductInternalTypeEnum.MEMBERSHIP as string)
                              ? "Buy"
                              : "Book"}
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <StripHTMLAndAddEllipses
                      content={product?.name}
                      maxLength={85}
                      className={
                        "relative sm:text14Semibold text12Semibold m-2 mt-auto text-whiteBlack-100 drop-shadow-sm z-10"
                      }
                    />
                  )}
                </div>
                {!isModal && (
                  <div
                    className={
                      "sm:text14Semibold text12Semibold shadow-[0px 2px 5px rgba(42, 93, 227, 0.3)] flex flex-row items-center justify-center gap-2 bg-primary-600 p-2.5 text-whiteBlack-100"
                    }
                    onClick={(e) => {
                      e.stopPropagation()
                      handleProductSelect(product)
                    }}
                  >
                    {product?.type?.internal_type ===
                      (ProductInternalTypeEnum.MERCHANDISE as string) ||
                    product?.type?.internal_type ===
                      (ProductInternalTypeEnum.GIFT_CARD as string) ||
                    product?.type?.internal_type === (ProductInternalTypeEnum.MEMBERSHIP as string)
                      ? "Buy"
                      : "Book "}
                  </div>
                )}
              </div>
            )
          })}
    </>
  )
}

export default CategoryProductPreview
